/**
 * @fileoverview Fórmulas e validações para cálculos DRE
 * @see RELATÓRIO 4 - DRE EDITÁVEL COM VERSIONAMENTO
 * @created 2025-10-17
 */

import { DREYear, DREValidationError, DREEditableField, ValidationState } from '../types/dre';

/**
 * Calcula todos os campos derivados da DRE baseado nos campos editáveis
 * 
 * LÓGICA BIDIRECIONAL INTELIGENTE:
 * - Se editar Despesas Operacionais → Calcula EBITDA
 * - Se editar EBITDA → Calcula Despesas Operacionais
 * - Se editar ambos → Prioriza o último editado (via lastEditedField)
 * 
 * @param year - Ano DRE com campos editáveis preenchidos
 * @param lastEditedField - Campo que o usuário acabou de editar (opcional)
 * @returns Objeto com campos calculados atualizados
 * 
 * @example
 * ```typescript
 * // Usuário editou Receita Bruta
 * const calculated = calculateDREFields(year, 'receitaBruta');
 * // → Calcula EBITDA baseado em Despesas
 * 
 * // Usuário editou EBITDA
 * const calculated = calculateDREFields(year, 'ebitda');
 * // → Calcula Despesas baseado em EBITDA
 * ```
 */
export function calculateDREFields(
  year: Partial<DREYear>, 
  lastEditedField?: string
): Partial<DREYear> {
  const {
    receitaBruta = 0,
    impostosVendas = 0,
    custosDirectos = 0,
    despesasOperacionais = 0,
    ebitda = 0,
    depreciacao = 0,
    impostosLucro = 0,
  } = year;

  // ========================================
  // PREMISSAS PADRÃO (de masterData.ts)
  // ========================================
  const PREMISSAS = {
    impostoVendas: 0.08,   // 8% da Receita Bruta
    margemCustos: 0.35,    // 35% da Receita Bruta
    depreciacao: 0.03,     // 3% da Receita Bruta
    impostoLucro: 0.18,    // 18% do EBIT
  };

  // ========================================
  // PARTE 1: RECÁLCULO PROPORCIONAL (se editou Receita Bruta)
  // ========================================
  
  let finalImpostosVendas = impostosVendas;
  let finalCustosDirectos = custosDirectos;
  let finalDepreciacao = depreciacao;
  
  // Se usuário editou Receita Bruta → Recalcular campos proporcionais
  if (lastEditedField === 'receitaBruta' && receitaBruta > 0) {
    // Impostos s/ Vendas sempre recalcula (8% fixo)
    finalImpostosVendas = receitaBruta * PREMISSAS.impostoVendas;
    
    // Custos: manter percentual anterior OU usar premissa se estava zerado
    if (custosDirectos === 0) {
      finalCustosDirectos = receitaBruta * PREMISSAS.margemCustos;
    } else {
      // Manter percentual customizado pelo usuário
      const percentualCustos = impostosVendas > 0 ? custosDirectos / (receitaBruta - impostosVendas + custosDirectos) : PREMISSAS.margemCustos;
      finalCustosDirectos = receitaBruta * percentualCustos;
    }
    
    // Depreciação: recalcular proporcionalmente
    finalDepreciacao = receitaBruta * PREMISSAS.depreciacao;
  }
  
  // ========================================
  // PARTE 2: CÁLCULOS BÁSICOS
  // ========================================
  
  // 1. Receita Líquida = Receita Bruta - Impostos s/ Vendas
  const receitaLiquida = receitaBruta - finalImpostosVendas;
  
  // 2. Lucro Bruto = Receita Líquida - Custos Diretos
  const lucroBruto = receitaLiquida - finalCustosDirectos;
  
  // 3. % Margem Bruta = (Lucro Bruto / Receita Líquida) × 100
  const margemBruta = receitaLiquida > 0 ? (lucroBruto / receitaLiquida) * 100 : 0;
  
  // ========================================
  // PARTE 2: LÓGICA BIDIRECIONAL (EBITDA vs DESPESAS)
  // ========================================
  
  let finalEbitda: number;
  let finalDespesasOperacionais: number;
  
  // Se o usuário editou EBITDA → Calcula Despesas
  if (lastEditedField === 'ebitda') {
    finalEbitda = ebitda;
    finalDespesasOperacionais = lucroBruto - ebitda;
  }
  // Se o usuário editou Despesas → Calcula EBITDA
  else if (lastEditedField === 'despesasOperacionais') {
    finalDespesasOperacionais = despesasOperacionais;
    finalEbitda = lucroBruto - despesasOperacionais;
  }
  // Caso padrão: Calcula EBITDA baseado em Despesas (comportamento original)
  else {
    finalDespesasOperacionais = despesasOperacionais;
    finalEbitda = lucroBruto - despesasOperacionais;
  }
  
  // ========================================
  // PARTE 3: CÁLCULOS SUBSEQUENTES
  // ========================================
  
  // 4. % Margem EBITDA = (EBITDA / Receita Líquida) × 100
  const margemEbitda = receitaLiquida > 0 ? (finalEbitda / receitaLiquida) * 100 : 0;
  
  // 5. EBIT = EBITDA - Depreciação
  const ebit = finalEbitda - finalDepreciacao;
  
  // 6. % Margem EBIT = (EBIT / Receita Líquida) × 100
  const margemEbit = receitaLiquida > 0 ? (ebit / receitaLiquida) * 100 : 0;
  
  // 7. Impostos s/ Lucro: recalcular se editou Receita Bruta
  let finalImpostosLucro = impostosLucro;
  if (lastEditedField === 'receitaBruta' && ebit > 0) {
    finalImpostosLucro = ebit * PREMISSAS.impostoLucro;
  }
  
  // 8. Lucro Líquido = EBIT - Impostos s/ Lucro
  const lucroLiquido = ebit - finalImpostosLucro;
  
  // 9. % Margem Líquida = (Lucro Líquido / Receita Líquida) × 100
  const margemLiquida = receitaLiquida > 0 ? (lucroLiquido / receitaLiquida) * 100 : 0;

  return {
    receitaBruta, // ✅ Retorna também campos editados (para store)
    impostosVendas: finalImpostosVendas,
    receitaLiquida,
    custosDirectos: finalCustosDirectos,
    lucroBruto,
    margemBruta,
    despesasOperacionais: finalDespesasOperacionais,
    ebitda: finalEbitda,
    margemEbitda,
    depreciacao: finalDepreciacao,
    ebit,
    margemEbit,
    impostosLucro: finalImpostosLucro,
    lucroLiquido,
    margemLiquida,
  };
}

/**
 * Valida integridade dos dados DRE
 * 
 * Validações implementadas:
 * - Receita Bruta > 0
 * - Impostos s/ Vendas ≤ Receita Bruta
 * - Custos Diretos ≤ Receita Líquida
 * - Despesas Operacionais não geram EBITDA muito negativo
 * - Valores negativos inesperados
 * 
 * @param year - Ano DRE completo para validar
 * @param company - ID da empresa ('setima' ou '555')
 * @returns Array de erros encontrados (vazio se válido)
 */
export function validateDRE(
  year: DREYear,
  company: 'setima' | '555'
): DREValidationError[] {
  const errors: DREValidationError[] = [];

  // ========================================
  // VALIDAÇÕES DE INTEGRIDADE
  // ========================================

  // 1. Receita Bruta deve ser maior que zero
  if (year.receitaBruta <= 0) {
    errors.push({
      company,
      year: year.year,
      field: 'receitaBruta',
      message: 'Receita Bruta deve ser maior que zero',
      severity: 'error',
    });
  }

  // 2. Impostos s/ Vendas não podem exceder Receita Bruta
  if (year.impostosVendas > year.receitaBruta) {
    errors.push({
      company,
      year: year.year,
      field: 'impostosVendas',
      message: `Impostos s/ Vendas (${formatCurrency(year.impostosVendas)}) excedem Receita Bruta (${formatCurrency(year.receitaBruta)})`,
      severity: 'error',
    });
  }

  // 3. Custos Diretos não podem exceder Receita Líquida
  if (year.custosDirectos > year.receitaLiquida) {
    errors.push({
      company,
      year: year.year,
      field: 'custosDirectos',
      message: `Custos Diretos (${formatCurrency(year.custosDirectos)}) excedem Receita Líquida (${formatCurrency(year.receitaLiquida)})`,
      severity: 'error',
    });
  }

  // 4. Warning: EBITDA negativo (despesas muito altas)
  if (year.ebitda < 0) {
    errors.push({
      company,
      year: year.year,
      field: 'despesasOperacionais',
      message: `EBITDA negativo (${formatCurrency(year.ebitda)}). Despesas operacionais muito altas.`,
      severity: 'warning',
    });
  }

  // 5. Warning: Margem Bruta < 20%
  if (year.margemBruta < 20 && year.margemBruta > 0) {
    errors.push({
      company,
      year: year.year,
      field: 'custosDirectos',
      message: `Margem Bruta baixa (${year.margemBruta.toFixed(1)}%). Custos diretos podem estar altos.`,
      severity: 'warning',
    });
  }

  // 6. Warning: Margem EBITDA < 10%
  if (year.margemEbitda < 10 && year.margemEbitda > 0) {
    errors.push({
      company,
      year: year.year,
      field: 'despesasOperacionais',
      message: `Margem EBITDA baixa (${year.margemEbitda.toFixed(1)}%). Empresa pouco rentável.`,
      severity: 'warning',
    });
  }

  // 7. Warning: Lucro Líquido negativo
  if (year.lucroLiquido < 0) {
    errors.push({
      company,
      year: year.year,
      field: 'lucroLiquido',
      message: `Lucro Líquido negativo (${formatCurrency(year.lucroLiquido)}). Empresa no prejuízo.`,
      severity: 'warning',
    });
  }

  // 8. 🆕 Warning: Depreciação zero em anos recentes (>= 2024)
  if (year.year >= 2024 && year.depreciacao === 0 && year.receitaBruta > 0) {
    const depreciacaoSugerida = year.receitaBruta * 0.03; // 3% da receita
    errors.push({
      company,
      year: year.year,
      field: 'depreciacao',
      message: `⚠️ Depreciação zero pode distorcer EBIT e valuation. Sugerido: ${formatCurrency(depreciacaoSugerida)} (3% da receita).`,
      severity: 'warning',
    });
  }

  return errors;
}

/**
 * Atualiza um campo editável e recalcula campos derivados
 * 
 * @param year - Ano DRE atual
 * @param field - Campo a atualizar
 * @param value - Novo valor
 * @returns Ano DRE atualizado com campos recalculados
 */
export function updateDREField(
  year: DREYear,
  field: DREEditableField,
  value: number
): DREYear {
  // Atualizar campo editável
  const updated = { ...year, [field]: value };
  
  // Recalcular campos derivados
  const calculated = calculateDREFields(updated);
  
  // Retornar ano completo atualizado
  return {
    ...updated,
    ...calculated,
  } as DREYear;
}

/**
 * Verifica se DRE tem dados válidos para valuation
 * 
 * @param years - Array de anos DRE
 * @returns true se tem pelo menos 3 anos com receita > 0
 */
export function hasValidDataForValuation(years: DREYear[]): boolean {
  const validYears = years.filter(y => y.receitaBruta > 0);
  return validYears.length >= 3;
}

/**
 * Calcula crescimento médio de receita (CAGR)
 * 
 * @param years - Array de anos DRE ordenados
 * @returns CAGR em % (ex: 15.5 = 15.5%)
 */
export function calculateRevenueCGR(years: DREYear[]): number {
  if (years.length < 2) return 0;
  
  const sorted = [...years].sort((a, b) => a.year - b.year);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  
  if (first.receitaBruta <= 0 || last.receitaBruta <= 0) return 0;
  
  const n = sorted.length - 1;
  const cagr = (Math.pow(last.receitaBruta / first.receitaBruta, 1 / n) - 1) * 100;
  
  return cagr;
}

/**
 * Calcula margem média de um período
 * 
 * @param years - Array de anos DRE
 * @param marginField - Campo de margem ('margemBruta', 'margemEbitda', etc.)
 * @returns Margem média em %
 */
export function calculateAverageMargin(
  years: DREYear[],
  marginField: 'margemBruta' | 'margemEbitda' | 'margemEbit' | 'margemLiquida'
): number {
  if (years.length === 0) return 0;
  
  const validYears = years.filter(y => y.receitaBruta > 0);
  if (validYears.length === 0) return 0;
  
  const sum = validYears.reduce((acc, y) => acc + y[marginField], 0);
  return sum / validYears.length;
}

// ========================================
// HELPERS DE FORMATAÇÃO
// ========================================

/**
 * Formata valor em moeda brasileira
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Formata valor como porcentagem
 */
export function formatPercent(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

// ========================================
// HELPERS DE RECÁLCULO
// ========================================

/**
 * Recalcula campos derivados de um ano DRE
 * (wrapper para calculateDREFields + merge)
 */
export function recalculateDREYear(year: DREYear): DREYear {
  const calculated = calculateDREFields(year);
  return { ...year, ...calculated };
}

/**
 * Valida array de anos DRE
 */
export function validateDREYears(
  years: DREYear[],
  company: 'setima' | '555'
): ValidationState {
  const allErrors: DREValidationError[] = [];
  
  for (const year of years) {
    const yearErrors = validateDRE(year, company);
    allErrors.push(...yearErrors);
  }
  
  // Separar por severidade
  const errors = allErrors.filter(e => e.severity === 'error');
  const warnings = allErrors.filter(e => e.severity === 'warning');
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    infos: [],
  };
}
